import { fork, put, takeLatest } from "redux-saga/effects";
import * as CashManagementProviderCustomerConfigApi from "../api/CashManagementProviderCustomerConfig";
import {
  CashManagementProviderCustomerConfig,
  CashManagementProviderCustomerConfigCredentials,
} from "../models/CashManagementProviderCustomerConfig";
import { startLoading, setSuccess, setError } from "../store/appSlice";
import {
  createCashManagementProviderCustomerConfig,
  fetchCashManagementProviderCustomerConfig,
  fetchCashManagementProviderCustomerConfigs,
  setCashManagementProviderCustomerConfig,
  setCashManagementProviderCustomerConfigs,
  updateCashManagementProviderCustomerConfig,
} from "../store/cashManagementProviderCustomerConfigSlice";
import { cashManagementProviderConfigOperationPaths } from "../store/cashManagementProviderCustomerConfigSlice";
import { UUID } from "../utils";
import { callApi } from "./helpers/api";
import { push } from "connected-react-router";

function* fetchProviderCustomerConfigsSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
  };
  type: string;
}) {
  const { customerId } = payload;
  const { listing: operationPath } = cashManagementProviderConfigOperationPaths(
    {
      customerId,
    }
  );

  yield put(startLoading(operationPath));

  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.getCashManagementProviderCustomerConfigs,
      customerId
    );
    yield put(setCashManagementProviderCustomerConfigs({ list: data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    configId: UUID;
  };
  type: string;
}): Generator<any> {
  const { configId } = payload;
  const { details: operationPath } = cashManagementProviderConfigOperationPaths(
    {
      configId,
    }
  );

  yield put(startLoading(operationPath));

  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.getCashManagementProviderCustomerConfig,
      configId
    );
    yield put(
      setCashManagementProviderCustomerConfig({
        id: { id: configId },
        item: data,
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
    providerId: UUID;
    data: CashManagementProviderCustomerConfigCredentials;
  };
  type: string;
}): Generator<any> {
  const { customerId, providerId, data: config } = payload;
  const { create: operationPath } = cashManagementProviderConfigOperationPaths({
    customerId,
    providerId,
  });
  yield put(startLoading(operationPath));
  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.postCashManagementProviderCustomerConfig,
      customerId,
      {
        cashManagementProvider: { id: providerId },
        credentials: config,
      } as CashManagementProviderCustomerConfig
    );
    yield put(
      setCashManagementProviderCustomerConfig({
        id: { id: data.id },
        item: data,
      })
    );
    yield put(
      push(
        `/configuration/customers/${customerId}/cash-management/provider-config/${data.id}`
      )
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    configId: UUID;
    providerId: UUID;
    data: CashManagementProviderCustomerConfigCredentials;
  };
  type: string;
}): Generator<any> {
  const { configId, data: config, providerId } = payload;
  const { update: operationPath } = cashManagementProviderConfigOperationPaths({
    configId,
  });
  yield put(startLoading(operationPath));
  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.putCashManagementProviderCustomerConfig,
      configId,
      {
        cashManagementProvider: { id: providerId },
        credentials: config,
      } as CashManagementProviderCustomerConfig
    );
    yield put(
      setCashManagementProviderCustomerConfig({
        id: { id: configId },
        item: data,
      })
    );
    yield put(
      push(
        `/configuration/customers/${data.customerId}/cash-management/provider-config/${configId}`
      )
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchProviderCustomerConfigs() {
  yield takeLatest(
    fetchCashManagementProviderCustomerConfigs.type,
    fetchProviderCustomerConfigsSaga
  );
}

function* watchFetchProviderCustomerConfig() {
  yield takeLatest(
    fetchCashManagementProviderCustomerConfig.type,
    fetchProviderCustomerConfigSaga
  );
}

function* watchCreateProviderCustomerConfig() {
  yield takeLatest(
    createCashManagementProviderCustomerConfig.type,
    createProviderCustomerConfigSaga
  );
}

function* watchUpdateProviderCustomerConfig() {
  yield takeLatest(
    updateCashManagementProviderCustomerConfig.type,
    updateProviderCustomerConfigSaga
  );
}

export function* CashManagementProviderCustomerConfigsSaga() {
  yield fork(watchFetchProviderCustomerConfigs);
  yield fork(watchFetchProviderCustomerConfig);
  yield fork(watchCreateProviderCustomerConfig);
  yield fork(watchUpdateProviderCustomerConfig);
}
